/**
 * The rate loop.
 *
 * Timings are normative, taken from design/Rate-Loop-Prototype.dc.html, which the handoff calls
 * behaviourally normative:
 *
 *   fill   0ms    the key IS the fill — no transition on the strip
 *   hold   250ms  score visible at full opacity
 *   out    120ms  opacity 1→0, translateY 0→−4px
 *   in     120ms  opacity 0→1, translateY 4px→0
 *   skip   a keypress during hold or out commits the advance INSTANTLY and rates the next
 *          song — the animation is skipped, never queued
 *
 * That last clause is the whole design. Every input path calls `flush()` first: cancel the
 * pending timer, apply its effect synchronously, then handle the new input. The animation must
 * never become a queue, because the user rates faster than it plays.
 *
 * The machine owns no I/O. Persistence is the outbox's job; this only decides what is on screen.
 */
export const HOLD_MS = 250;
export const MOVE_MS = 120;
export const REVEAL_MS = 400;
/** At this many lifetime skips the card offers the escape hatch. */
export const SKIP_PROMPT_AT = 3;
export class RateMachine {
    queue;
    qi = 0;
    ratings;
    hover = null;
    phase = "idle";
    undoStack = [];
    reduced;
    skipCounts;
    favourites;
    pending = null;
    timers;
    now;
    onRated;
    onUndone;
    onChange;
    onSkipped;
    onFavourited;
    constructor(options) {
        this.queue = [...options.queue];
        this.ratings = { ...(options.ratings ?? {}) };
        this.reduced = options.reduced ?? false;
        // Wrapped, not passed by reference: browsers require setTimeout to be invoked with `window`
        // as its receiver and throw "Illegal invocation" otherwise. Node does not care, which is
        // exactly why this survived the unit tests and only surfaced in a real browser.
        this.timers = options.timers ?? {
            setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
            clearTimeout: (handle) => globalThis.clearTimeout(handle),
        };
        this.now = options.now ?? (() => new Date());
        this.onRated = options.onRated;
        this.onUndone = options.onUndone;
        this.onChange = options.onChange;
        this.onSkipped = options.onSkipped;
        this.skipCounts = { ...(options.skipCounts ?? {}) };
        this.onFavourited = options.onFavourited;
        this.favourites = { ...(options.favourites ?? {}) };
    }
    /**
     * Whether the current card should offer "Can't place this?" — the Spotify search link and a
     * Never queue this option. Three skips is the app noticing that you keep bouncing off a song,
     * which usually means you do not actually know it or it is an uncollapsed duplicate.
     */
    get shouldOfferHelp() {
        const id = this.currentSongId;
        return id !== null && (this.skipCounts[id] ?? 0) >= SKIP_PROMPT_AT;
    }
    /** The song under the cursor, or null when the queue is exhausted. */
    get currentSongId() {
        return this.queue[this.qi] ?? null;
    }
    get remaining() {
        return Math.max(0, this.queue.length - this.qi);
    }
    /** What the strip should paint: the hover value if hovering, else the committed rating. */
    get displayValue() {
        if (this.hover !== null)
            return this.hover;
        const id = this.currentSongId;
        return id === null ? null : (this.ratings[id] ?? null);
    }
    /** True while the just-rated score is still on screen — the strip paints vermilion, not ink. */
    get isLive() {
        return this.hover !== null || this.phase === "hold" || this.phase === "out";
    }
    /**
     * Rate the current song. The fill is immediate and unconditional; the advance is what waits.
     * A second call while an advance is pending commits that advance first, so a fast typist
     * never has keystrokes swallowed or replayed out of order.
     */
    rate(value) {
        if (value < 1 || value > 10)
            throw new RangeError(`rating must be 1..10, got ${value}`);
        this.flush();
        const songId = this.currentSongId;
        if (songId === null)
            return;
        const previous = this.ratings[songId] ?? null;
        this.ratings[songId] = value;
        this.hover = null;
        this.undoStack.push({ songId, previous, queueIndex: this.qi });
        this.onRated?.({ songId, value, previous, ratedAt: this.now() });
        if (this.reduced) {
            // No choreography: the state simply changes.
            this.advance();
            this.emit();
            return;
        }
        this.phase = "hold";
        this.pending = {
            songId,
            outStarted: false,
            handle: this.timers.setTimeout(() => this.onHoldElapsed(), HOLD_MS),
        };
        this.emit();
    }
    onHoldElapsed() {
        if (!this.pending)
            return;
        this.phase = "out";
        this.pending.outStarted = true;
        this.pending.handle = this.timers.setTimeout(() => this.commit(), MOVE_MS);
        this.emit();
    }
    /** Ends the pending advance immediately, wherever it had got to. */
    flush() {
        if (!this.pending)
            return;
        this.timers.clearTimeout(this.pending.handle);
        this.pending = null;
        this.advance();
        this.phase = "idle";
    }
    commit() {
        this.pending = null;
        this.advance();
        // The incoming card starts displaced and settles; the caller clears `in` on the next frame.
        this.phase = "in";
        this.emit();
    }
    /** Called by the view once the incoming card has been painted at its start position. */
    settle() {
        if (this.phase === "in") {
            this.phase = "idle";
            this.emit();
        }
    }
    advance() {
        if (this.qi < this.queue.length)
            this.qi += 1;
        this.hover = null;
    }
    /**
     * `S` — leaves the song unrated, drops it from this sitting, and increments its lifetime skip
     * count. Distinct from the arrow keys on purpose: an arrow is navigation, S is a judgement that
     * you cannot rate this right now, and that judgement is the only signal explaining why a song
     * keeps coming back.
     */
    skip() {
        this.flush();
        const songId = this.currentSongId;
        if (songId === null)
            return null;
        const skipCount = (this.skipCounts[songId] ?? 0) + 1;
        this.skipCounts[songId] = skipCount;
        this.onSkipped?.({ songId, skipCount, prompt: skipCount >= SKIP_PROMPT_AT });
        this.queue.splice(this.qi, 1);
        if (this.qi > this.queue.length)
            this.qi = this.queue.length;
        this.hover = null;
        this.phase = "idle";
        this.emit();
        return songId;
    }
    /** True when the song under the cursor is a favourite. */
    get isFavourite() {
        const id = this.currentSongId;
        return id !== null && this.favourites[id] === true;
    }
    /**
     * `F` — favourite.
     *
     * Deliberately does NOT flush: every other key here commits a pending rating early because it
     * means "I am done with that song and moving on". F means the opposite — it is about the song
     * on screen right now, and flushing would advance past it mid-hold and favourite the wrong one.
     *
     * It also does not rate, undo, requeue or advance. It is a bookmark, and the strip is the only
     * thing in this app that says how good a song is.
     */
    toggleFavourite() {
        const songId = this.currentSongId;
        if (songId === null)
            return null;
        const favourite = !this.favourites[songId];
        this.favourites[songId] = favourite;
        // The resolved state, not the key press: a retried toggle would flip the song back.
        this.onFavourited?.({ songId, favourite });
        this.emit();
        return favourite;
    }
    /** `←` `→` — pure cursor movement, no effect on ratings or queue membership. */
    step(delta) {
        this.flush();
        this.qi = Math.min(this.queue.length, Math.max(0, this.qi + delta));
        this.hover = null;
        this.phase = "idle";
        this.emit();
    }
    /**
     * `Z` — pops the stack, restores the previous value, and re-queues the song at the position it
     * was rated from, so undo returns you to where you were rather than to the end.
     */
    undo() {
        this.flush();
        const entry = this.undoStack.pop();
        if (!entry)
            return false;
        if (entry.previous === null)
            delete this.ratings[entry.songId];
        else
            this.ratings[entry.songId] = entry.previous;
        if (!this.queue.includes(entry.songId)) {
            this.queue.splice(entry.queueIndex, 0, entry.songId);
        }
        this.qi = Math.max(0, Math.min(entry.queueIndex, this.queue.length - 1));
        this.hover = null;
        this.phase = "idle";
        this.onUndone?.(entry.songId, entry.previous);
        this.emit();
        return true;
    }
    setHover(value) {
        this.hover = value;
        this.emit();
    }
    /** Releases any pending timer. Call on unmount. */
    dispose() {
        if (this.pending)
            this.timers.clearTimeout(this.pending.handle);
        this.pending = null;
    }
    emit() {
        this.onChange?.();
    }
}
/** Eased progress for the score reveal: 1 − (1 − t)³, per the prototype. */
export function revealProgress(elapsedMs, durationMs = REVEAL_MS) {
    const t = Math.min(1, Math.max(0, elapsedMs / durationMs));
    return 1 - (1 - t) ** 3;
}
/** How many spark bars are drawn at a given progress — left to right, ~29ms apart. */
export function revealBars(progress, total) {
    return Math.ceil(progress * total);
}
//# sourceMappingURL=rate-machine.js.map